import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";

export const userManagementSlide = createSlice({
    name: "userManagement",
    initialState: {
        page: 0,
        pageCount: 0,
        success: false,
        fail: false,
        listData: null,
        statsData: null,
        detailData: null,
        activityData: null,
        responseData: null,
        errorDetailData: null,
        statusUpdateSuccess: false,
        exporting: false,
    },
    reducers: {
        setUserManagementExporting: (state, action) => {
            state.exporting = action.payload;
        },
        doGetUserManagementListSuccess: (state, action) => {
            state.listData = action.payload.data;
            state.page = action.payload.current_page;
            state.pageCount = action.payload.page_count;
            state.total = action.payload.total_row;
        },
        doGetUserManagementListFail: (state, action) => {
            state.listData = null;
            state.page = 0;
            state.pageCount = 0;
            state.total = 0;
        },
        doGetUserManagementStatsSuccess: (state, action) => {
            state.statsData = action.payload;
        },
        doGetUserManagementStatsFail: (state, action) => {
            state.statsData = null;
        },
        doGetUserManagementDetailSuccess: (state, action) => {
            state.detailData = action.payload.user;
        },
        doGetUserManagementDetailFail: (state, action) => {
            state.detailData = null;
        },
        doGetUserManagementActivitySuccess: (state, action) => {
            state.activityData = action.payload;
        },
        doGetUserManagementActivityFail: (state, action) => {
            state.activityData = null;
        },
        doAddUserManagementSuccess: (state, action) => {
            state.success = true;
            state.fail = false;
            state.responseData = action.payload;
        },
        doAddUserManagementFail: (state, action) => {
            state.success = false;
            state.fail = true;
            state.errorDetailData = action.payload;
        },
        doUpdateUserManagementSuccess: (state, action) => {
            state.success = true;
            state.fail = false;
        },
        doUpdateUserManagementFail: (state, action) => {
            state.success = false;
            state.fail = true;
            state.errorDetailData = action.payload;
        },
        doUpdateUserManagementStatusSuccess: (state, action) => {
            state.statusUpdateSuccess = true;
        },
        doUpdateUserManagementStatusFail: (state, action) => {
            state.statusUpdateSuccess = false;
            state.errorDetailData = action.payload;
        },
        resetUserManagementStateSuccess: (state, action) => {
            state.success = action.payload;
            state.fail = action.payload;
            state.detailData = null;
            state.activityData = null;
            state.errorDetailData = null;
            state.statusUpdateSuccess = false;
        },
    },
});

export const doGetUserManagementList = (param) => async (dispatch) => {
    try {
        let url = `/api/admin/user-management?page=${param.page}&size=${param.limit}`;
        if (param.keyword) {
            url += `&keyword=${encodeURIComponent(param.keyword)}`;
        }
        if (param.role) {
            url += `&role=${param.role}`;
        }
        if (param.status) {
            url += `&status=${param.status}`;
        }
        if (param.plan) {
            url += `&plan=${encodeURIComponent(param.plan)}`;
        }
        if (param.join_from) {
            url += `&join_from=${param.join_from}`;
        }
        if (param.join_to) {
            url += `&join_to=${param.join_to}`;
        }
        if (param.sort) {
            url += `&sort=${param.sort}`;
        }

        const response = await axios.get(url);
        dispatch(doGetUserManagementListSuccess(response.data));
    } catch (error) {
        dispatch(doGetUserManagementListFail(error.response));
    }
};

// Download the filtered users as a CSV file (streamed as a blob).
export const doExportUserManagement = (param) => async (dispatch) => {
    try {
        dispatch(setUserManagementExporting(true));

        let url = `/api/admin/user-management/export?role=${param.role}`;
        if (param.keyword) {
            url += `&keyword=${encodeURIComponent(param.keyword)}`;
        }
        if (param.status) {
            url += `&status=${param.status}`;
        }
        if (param.plan) {
            url += `&plan=${encodeURIComponent(param.plan)}`;
        }
        if (param.join_from) {
            url += `&join_from=${param.join_from}`;
        }
        if (param.join_to) {
            url += `&join_to=${param.join_to}`;
        }
        if (param.sort) {
            url += `&sort=${param.sort}`;
        }

        const response = await axios.get(url, { responseType: "blob" });

        const blob = new Blob([response.data], { type: "text/csv;charset=utf-8" });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        const isHost = param.role === "HOST";
        const ym = `${new Date().getFullYear()}${String(new Date().getMonth() + 1).padStart(2, "0")}`;
        link.setAttribute("download", `${isHost ? "hosts" : "tenants"}_${ym}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
        // no-op: keep the UI responsive even if the export fails
    } finally {
        dispatch(setUserManagementExporting(false));
    }
};

export const doGetUserManagementStats = () => async (dispatch) => {
    try {
        const response = await axios.get(`/api/admin/user-management/stats`);
        dispatch(doGetUserManagementStatsSuccess(response.data));
    } catch (error) {
        dispatch(doGetUserManagementStatsFail(error.response));
    }
};

export const doGetUserManagementDetail = (param) => async (dispatch) => {
    try {
        const url = `/api/admin/user-management/${param.user_id}`;
        const response = await axios.get(url);
        dispatch(doGetUserManagementDetailSuccess(response.data));
    } catch (error) {
        dispatch(doGetUserManagementDetailFail(error.response));
    }
};

export const doGetUserManagementActivity = (param) => async (dispatch) => {
    try {
        const url = `/api/admin/user-management/${param.user_id}/activity`;
        const response = await axios.get(url);
        dispatch(doGetUserManagementActivitySuccess(response.data));
    } catch (error) {
        dispatch(doGetUserManagementActivityFail(error.response));
    }
};

export const doAddUserManagement = (param) => async (dispatch) => {
    try {
        const url = `/api/admin/user-management`;
        const response = await axios.post(url, param);
        dispatch(doAddUserManagementSuccess(response.data));
    } catch (error) {
        dispatch(doAddUserManagementFail(error.response));
    }
};

export const doUpdateUserManagement = (param, userId) => async (dispatch) => {
    try {
        const url = `/api/admin/user-management/${userId}`;
        const response = await axios.put(url, param);
        dispatch(doUpdateUserManagementSuccess(response.data));
    } catch (error) {
        dispatch(doUpdateUserManagementFail(error.response));
    }
};

export const doUpdateUserManagementStatus = (userId, status) => async (dispatch) => {
    try {
        const url = `/api/admin/user-management/${userId}/status`;
        const response = await axios.patch(url, { status });
        dispatch(doUpdateUserManagementStatusSuccess(response.data));
    } catch (error) {
        dispatch(doUpdateUserManagementStatusFail(error.response));
    }
};

export const resetUserManagementState = () => async (dispatch) => {
    dispatch(resetUserManagementStateSuccess(false));
};

export const {
    setUserManagementExporting,
    doGetUserManagementListSuccess,
    doGetUserManagementListFail,
    doGetUserManagementStatsSuccess,
    doGetUserManagementStatsFail,
    doGetUserManagementDetailSuccess,
    doGetUserManagementDetailFail,
    doGetUserManagementActivitySuccess,
    doGetUserManagementActivityFail,
    doAddUserManagementSuccess,
    doAddUserManagementFail,
    doUpdateUserManagementSuccess,
    doUpdateUserManagementFail,
    doUpdateUserManagementStatusSuccess,
    doUpdateUserManagementStatusFail,
    resetUserManagementStateSuccess,
} = userManagementSlide.actions;

export const getUserManagementList = (state) => state.userManagement?.listData;
export const getUserManagementStats = (state) => state.userManagement?.statsData;
export const getUserManagementListPage = (state) => state.userManagement?.page;
export const getUserManagementListPageCount = (state) => state.userManagement?.pageCount;
export const getUserManagementListTotal = (state) => state.userManagement?.total;
export const getUserManagementDetail = (state) => state.userManagement?.detailData;
export const getUserManagementActivity = (state) => state.userManagement?.activityData;
export const getUserManagementErrorDetailData = (state) => state.userManagement?.errorDetailData;
export const getAddUserManagementSuccess = (state) => state.userManagement?.success;
export const getAddUserManagementFail = (state) => state.userManagement?.fail;
export const getAddUserManagementResponseData = (state) => state.userManagement?.responseData;
export const getStatusUpdateSuccess = (state) => state.userManagement?.statusUpdateSuccess;
export const getUserManagementExporting = (state) => state.userManagement?.exporting;

export default userManagementSlide.reducer;
